import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, pcs, phrase, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; stab: readonly string[]; pad: readonly string[]; gtr: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  Am7: { root: 'A1', stab: ['C4', 'E4', 'G4', 'A4'], pad: ['A3', 'C4', 'E4', 'G4'], gtr: ['G4', 'C5', 'E5'] },
  D7: { root: 'D2', stab: ['C4', 'F#4', 'A4', 'D5'], pad: ['D3', 'A3', 'C4', 'F#4'], gtr: ['F#4', 'C5', 'E5'] },
  Dm7: { root: 'D2', stab: ['C4', 'F4', 'A4', 'D5'], pad: ['D3', 'A3', 'C4', 'F4'], gtr: ['F4', 'C5', 'E5'] },
  G7: { root: 'G1', stab: ['B3', 'D4', 'F4', 'G4'], pad: ['G3', 'B3', 'D4', 'F4'], gtr: ['F4', 'B4', 'D5'] },
  Cmaj7: { root: 'C2', stab: ['B3', 'C4', 'E4', 'G4'], pad: ['C3', 'G3', 'B3', 'E4'], gtr: ['E4', 'G4', 'B4'] },
  Fmaj7: { root: 'F1', stab: ['A3', 'C4', 'E4', 'F4'], pad: ['F3', 'A3', 'C4', 'E4'], gtr: ['E4', 'A4', 'C5'] },
  Em7: { root: 'E2', stab: ['B3', 'D4', 'E4', 'G4'], pad: ['E3', 'B3', 'D4', 'G4'], gtr: ['D4', 'G4', 'B4'] },
  E7: { root: 'E2', stab: ['B3', 'D4', 'E4', 'G#4'], pad: ['E3', 'B3', 'D4', 'G#4'], gtr: ['D4', 'G#4', 'B4'] },
};

const PROGS: readonly (readonly string[])[] = [
  ['Am7', 'Am7', 'D7', 'D7'],
  ['Am7', 'Dm7', 'G7', 'Cmaj7'],
  ['Fmaj7', 'Em7', 'Dm7', 'Cmaj7'],
  ['Dm7', 'G7', 'Em7', 'Am7'],
  ['Am7', 'Fmaj7', 'Dm7', 'E7'],
];

const HOOK: Note[][] = [
  [[0, 'E5', 2], [2, 'G5', 1], [3, 'A5', 3], [6, 'G5', 2], [8, 'E5', 2], [11, 'D5', 1, 0.8], [12, 'E5', 4]],
  [[0, 'C5', 2], [2, 'D5', 1, 0.8], [3, 'E5', 3], [6, 'G5', 2], [10, 'E5', 2], [12, 'D5', 2], [14, 'C5', 2]],
  [[0, 'F#5', 2], [2, 'A5', 1], [3, 'F#5', 3], [6, 'E5', 2], [8, 'D5', 2], [11, 'C5', 1, 0.8], [12, 'D5', 4]],
  [[0, 'A4', 2], [2, 'C5', 1, 0.8], [3, 'D5', 3], [6, 'F#5', 2], [10, 'E5', 2], [12, 'D5', 2], [14, 'C5', 2]],
];

const BASSES: readonly (readonly [step: number, semis: number, len: number][])[] = [
  [[0, 0, 1.5], [2, 12, 1.5], [4, 0, 1.5], [6, 12, 1.5], [8, 0, 1.5], [10, 12, 1.5], [12, 0, 1.5], [14, 12, 1.5]],
  [[0, 0, 1.5], [2, 12, 1.5], [4, 0, 1.5], [6, 12, 1.5], [8, 0, 1.5], [10, 12, 1.5], [12, 0, 1], [13, 0, 1], [14, 12, 1.5]],
  [[0, 0, 1.5], [2, 12, 1.5], [3, 0, 1], [4, 0, 1.5], [6, 12, 1.5], [8, 0, 1.5], [10, 12, 1.5], [11, 7, 1], [12, 0, 1.5], [14, 12, 1.5]],
];

const KEYS = [0, 2, -2, 3, -3, 1, -1] as const;
const CLAPS = ['....X.......X...', '....X.......X..x'] as const;
const TAMBS = ['o.o.x.o.o.o.x.o.', 'o.o.x.o.o.oox.o.'] as const;
const GTRS = ['..X...X...X...X.', '..X..X.X..X..X.X', '.X.X..X..X.X..X.'] as const;
const STABS = ['X.....x.....x...', 'X.......x..x....', '......X.....X...'] as const;
const CLAVS = ['x..x..x...x.x...', '.x..x..x.x..x...'] as const;
const CONGA_HI = '..x..x.x..x...x.';
const CONGA_LO = '......X.......X.';
const MINOR = [9, 11, 0, 2, 4, 5, 7] as const;
const DORIAN = [9, 11, 0, 2, 4, 6, 7] as const;

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'groove', bars: 8, min: 4, rank: 3 },
    { name: 'build', bars: 2, min: 2, rank: 4 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 2, rank: 5, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 2 },
  ],
};

function score(seed: string, p: Params): Score {
  const ch = chooser(DISCO, seed);
  const key = ch.pick(KEYS);
  const names = ch.pick(PROGS);
  const prog = names.map((n) => CHORDS[n]!);
  const bassRow = ch.pick(BASSES);
  const clapP = ch.pick(CLAPS);
  const tambP = ch.pick(TAMBS);
  const gtrP = ch.pick(GTRS);
  const stabP = ch.pick(STABS);
  const clavP = ch.pick(CLAVS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs([c.root, ...c.stab]) }]),
        names.includes('D7') ? DORIAN : MINOR,
        [69, 81],
        76,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 52, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const outro = total - form[form.length - 1]!.bars;
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const build = find(form, 'build');
  const brk = find(form, 'break');
  const groove = find(form, 'groove');
  const rng = createRng(`disco-${seed}`);
  const human = (v: number) => Math.min(1, v * (0.93 + rng.float() * 0.12));

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number, jitter = 0) =>
    events.push({ v, t: g.at(bar, step) + (jitter === 0 ? 0 : rng.range(-jitter, jitter)), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes });

  const autos = [
    { v: 'strLp', points: [[g.at(0), 600], [g.at(intro), 2500], [g.at(drop1), 4500], [g.at(outro), 4500], [g.at(total), 700]] },
  ] as Score['autos'];

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const c = prog[i]!;
    const inDrop = b.name === 'drop' || b.name === 'drop2';
    const inGroove = b.name === 'groove';
    const building = b.name === 'build';
    const inBreak = b.name === 'break';
    const outroBody = b.name === 'outro' && !b.last;
    const introKick = b.name === 'intro' && b.index >= b.bars / 2;
    const band = inDrop || inGroove || outroBody;
    const hatOpen = band || introKick;

    if (band || introKick || (building && !b.last)) {
      for (const h of hits('X...X...X...X...')) hit('kick', b.bar, h.step, h.vel);
    } else if (building) {
      for (const h of hits('X...X...........')) hit('kick', b.bar, h.step, h.vel);
    }
    if (band || building) for (const h of hits(clapP)) hit('clap', b.bar, h.step, human(h.vel));
    if (inDrop || outroBody) for (const h of hits('....x.......x...')) hit('snare', b.bar, h.step, human(0.6));
    if (hatOpen) for (const h of hits('..X...X...X...X.')) hit('hatO', b.bar, h.step, human(h.vel));
    if (band || building || b.name === 'intro') {
      const pattern = e >= 0.4 && !(b.name === 'intro') ? 'xoxoxoxoxoxoxoxo' : 'x.o.x.o.x.o.x.o.';
      for (const h of hits(pattern)) {
        if (hatOpen && h.step % 4 === 2) continue;
        hit('hatC', b.bar, h.step, human(h.vel), 0.002);
      }
    }
    if (inDrop && e >= 0.5) for (const h of hits(tambP)) hit('tamb', b.bar, h.step, human(h.vel), 0.003);
    if (inBreak || (inDrop && e >= 0.6)) {
      for (const h of hits(CONGA_HI)) hit('congaHi', b.bar, h.step, human(h.vel), 0.004);
      for (const h of hits(CONGA_LO)) hit('congaLo', b.bar, h.step, human(h.vel), 0.004);
    }

    if (band || (building && !b.last)) {
      for (const [step, semis, len] of bassRow) {
        play('bass', b.bar, step, tr(transpose(c.root, semis + 12)), len * s * 0.9, human(semis === 12 ? 0.8 : 0.95));
        if (semis === 0) play('sub', b.bar, step, tr(c.root), len * s * 0.9, 0.85);
      }
    }

    const gtr = c.gtr.map(tr);
    if (band) {
      const marks = new Set(hits(gtrP).map((h) => h.step));
      for (let step = 0; step < 16; step++) {
        if (marks.has(step)) chord('gtr', b.bar, step, gtr, s * 0.6, human(0.85));
        else if (inDrop || step % 2 === 0) hit('mute', b.bar, step, human(step % 2 === 0 ? 0.5 : 0.3), 0.002);
      }
    }
    if (inDrop && e >= 0.6) for (const h of hits(clavP)) chord('clav', b.bar, h.step, gtr.map((n) => transpose(n, -12)), s * 0.7, human(h.vel));

    if (b.name === 'intro' || inBreak || building || inDrop || b.name === 'outro') {
      chord('strings', b.bar, 0, c.pad.map(tr), g.barLength * 0.98, inBreak ? 0.9 : inDrop ? 0.5 : 0.7);
    }

    const stab = c.stab.map(tr);
    if (inGroove || inDrop) for (const h of hits(stabP)) chord('stab', b.bar, h.step, stab, s * 1.5, human(inGroove ? h.vel * 0.8 : h.vel));

    if (b.bar === drop1 - 1 || (drop2 !== undefined && b.bar === drop2 - 1)) {
      const next = prog[cycle(b.bar + 1 - intro, 4)]!.stab.map(tr);
      const run = [...next, ...next.map((n) => transpose(n, 12))];
      run.forEach((n, k) => play('run', b.bar, 8 + k, n, s * 0.9, 0.55 + k * 0.05));
    }

    const row = hook[i]!;
    if (inBreak && b.index >= b.bars / 2) {
      for (const [step, note, len, vel] of row) play('line', b.bar, step, tr(note), len * s * 0.95, (vel ?? 0.9) * 0.8);
    }
    if (inDrop) {
      const octave = (b.name === 'drop2' && e >= 0.6) || e >= 0.85;
      for (const [step, note, len, vel] of row) {
        play('lead', b.bar, step, tr(note), len * s * 0.9, human(vel ?? 0.95));
        if (octave) play('lead', b.bar, step, tr(transpose(note, -12)), len * s * 0.9, (vel ?? 0.9) * 0.5);
      }
    }

    const grain = tr(transpose(c.root, 24));
    if (building) {
      for (let step = 0; step < 16; step++) {
        const at = (b.index * 16 + step) / (b.bars * 16);
        if (at < 0.75 && step % 2 === 1) continue;
        const dur = at < 0.75 ? s * 1.8 : s * 0.9;
        play('scratch', b.bar, step, grain, dur, 0.45 + 0.5 * at);
        if (e >= 0.6 && step % 2 === 0) chord('stab', b.bar, step, stab, s * 0.8, 0.4 + 0.45 * at);
      }
    }
    if (inDrop && b.index % 4 === 3 && (b.index % 8 === 7 || e >= 0.85)) {
      for (const step of [8, 10, 12, 13, 14, 15]) play('scratch', b.bar, step, grain, step < 12 ? s * 1.8 : s * 0.9, step < 12 ? 0.7 : 0.85);
    }
    if (inGroove && b.last) for (const step of [12, 14]) play('scratch', b.bar, step, grain, s * 1.8, 0.7);
  }

  const riseFrom = build ?? Math.max(0, drop1 - 2);
  if (riseFrom < drop1) events.push({ v: 'rise', t: g.at(riseFrom), end: g.at(drop1) - s * 2, bar: riseFrom, step: 0, vel: 1 });
  if (drop2 !== undefined) events.push({ v: 'rise', t: g.at(drop2 - 2), end: g.at(drop2) - s * 2, bar: drop2 - 2, step: 0, vel: 1 });
  if (brk !== undefined) events.push({ v: 'fall', t: g.at(brk), end: g.at(brk + 2), bar: brk, step: 0, vel: 1 });
  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('crash', bar, 0, 1);
    hit('impact', bar, 0, 1);
  }
  if (groove !== undefined) hit('crash', groove, 0, 0.7);

  return {
    id: trackId(DISCO, seed, p),
    template: DISCO.id,
    seed,
    params: p,
    title: ch.orig ? DISCO.ref.title : titleOf(seed),
    genre: DISCO.genre,
    bpm: p.bpm,
    swing: 52,
    lufs: -10.5,
    key,
    form,
    mix: { reverbDecay: 2.4, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.25, drive: 1.5, softness: 1.25, pumpDepth: 0.4, pumpRelease: 0.24 },
    events,
    autos,
    accents: { kick: 'kick', clap: 'snare', stab: 'stab', lead: 'melody', impact: 'drop' },
  };
}

export const DISCO: Template = {
  id: 'disco',
  genre: 'disco',
  bpm: [112, 130],
  ref: { seed: 'vinil', title: 'Vinil', params: { bpm: 120, bars: 28, energy: 0.55 } },
  score,
};
